/**
 * PDP free gift block: offer messaging, gift pickers, and form guard.
 *
 * Picker modes:
 * - Auto-add + multi-SKU: mandatory selection (blocks add-to-cart until valid).
 *   Choose-one radios pre-select the first available gift when nothing is stored.
 * - promptToAdd: selection required on PDP.
 * - Deterministic single-gift offers: auto-selected and synced to form payload.
 */
class FreeGiftOffer extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    if (!window.FreeGiftShared) {
      window.addEventListener(
        "load",
        () => {
          if (this._initialized) return;
          this._initialized = true;
          this._init();
        },
        { once: true }
      );
      return;
    }
    this._initialized = true;
    this._init();
  }

  disconnectedCallback() {
    if (this._cartUnsubscribe) {
      this._cartUnsubscribe();
      this._cartUnsubscribe = null;
    }
    if (this._giftAddUnsubscribe) {
      this._giftAddUnsubscribe();
      this._giftAddUnsubscribe = null;
    }
  }

  _labels() {
    const strings = window.freeGiftStrings || {};
    return {
      chooseHeading:
        this.dataset.chooseHeading ||
        strings.choose_heading ||
        "Choose your free gift",
      chooseOne:
        this.dataset.chooseOne ||
        strings.choose_one ||
        "Select one free gift before adding to cart.",
      chooseMany:
        this.dataset.chooseMany ||
        strings.choose_many ||
        "Select your free gifts before adding to cart.",
      selectedCount:
        this.dataset.selectedCount ||
        strings.selected_count ||
        "{{ selected }} of {{ total }} selected",
      soldOut: this.dataset.soldOut || strings.sold_out || "Sold out",
      addOne: this.dataset.addOne || strings.add_one || "Add",
      clear: this.dataset.clear || strings.clear || "Clear",
      selectionRequired:
        this.dataset.selectionRequired ||
        strings.selection_required ||
        "Please choose your free gift before adding to cart.",
      optionalHint:
        this.dataset.optionalHint ||
        strings.prompt_optional_hint ||
        "Optional — select a free gift to include when you add to cart.",
      alreadyInCart:
        this.dataset.alreadyInCartLabel ||
        strings.already_in_cart ||
        "Free gift already in cart",
      blockHeading: this.dataset.blockHeading || strings.heading || "Free Gift",
      upToFree:
        this.dataset.upToFree || strings.up_to_free || "Up to {{ count }} free",
    };
  }

  async _init() {
    try {
      const Shared = window.FreeGiftShared;
      if (!Shared) return;

      const offers = this._parseOffers();
      if (!offers.length) {
        this._removeBlock();
        return;
      }

      const cart = await this._fetchCart();
      this._cartContext = this._cartSnapshot(cart);

      const resolvedOffers = await Promise.all(
        offers.map((offer) => this._resolveOffer(offer))
      );

      const validOffers = resolvedOffers.filter(
        (offer) =>
          offer &&
          Array.isArray(offer.gifts) &&
          offer.gifts.some((gift) => gift.available)
      );
      if (!validOffers.length) {
        this._removeBlock();
        return;
      }

      this._offers = validOffers;
      this._render(validOffers);
      this._bindCartListener();
      this._bindProductFormGuard();
      this._bindPdpGiftOnProductAdd();
    } catch (error) {
      console.warn("[free-gift-offer]", error);
      this._removeBlock();
    }
  }

  _cartSnapshot(cart) {
    const Shared = window.FreeGiftShared;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return {
      items,
      subtotalCents: Shared
        ? Shared.cartSubtotalCents(cart || { items: [] })
        : 0,
    };
  }

  async _fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
    try {
      const response = await fetch(`${cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return { items: [] };
      return response.json();
    } catch {
      return { items: [] };
    }
  }

  _parseOffers() {
    const script = this.querySelector("[data-free-gift-offers]");
    if (!script) return [];
    return window.FreeGiftShared
      ? window.FreeGiftShared.parseOffersJson(script.textContent.trim())
      : [];
  }

  async _resolveOffer(offer) {
    const Shared = window.FreeGiftShared;
    if (!Shared) return null;

    let resolvedGifts = [];
    try {
      resolvedGifts = Shared.resolveOfferGifts
        ? await Shared.resolveOfferGifts(offer)
        : [];
    } catch {
      resolvedGifts = [];
    }

    if (!resolvedGifts.length) return null;

    return {
      raw: offer,
      title: offer.title || "",
      itemMessage: offer.itemMessage || "",
      gifts: resolvedGifts,
    };
  }

  _formatMoney(cents) {
    const format = this.dataset.moneyFormat || "${{amount}}";
    const amount = (Number(cents) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents) / 100));
    const amountWithCommaSeparators = Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const amountWithApostropheSeparators = amountWithCommaSeparators.replace(
      /,/g,
      "'"
    );

    return format
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        amountWithCommaSeparators
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        amountNoDecimals
      )
      .replace(
        /\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,
        amountWithApostropheSeparators
      )
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  _worthHeading(availableGifts) {
    const template = this.dataset.worthLabel || "Free Gift Worth {{ value }}";
    const maxPrice = availableGifts.reduce(
      (max, gift) => Math.max(max, gift.price || 0),
      0
    );
    return template.replace(
      /\{\{\s*value\s*\}\}/g,
      this._formatMoney(maxPrice)
    );
  }

  _hideSkeleton() {
    const skeleton = this.querySelector("[data-free-gift-skeleton]");
    if (skeleton) skeleton.hidden = true;
    this.classList.add("free-gift-offer--hydrated");
    this.setAttribute("aria-busy", "false");
  }

  _removeBlock() {
    this._hideMediaBadge();
    this.remove();
  }

  _hideMediaBadge() {
    const productRoot = this.closest(".product");
    if (!productRoot) return;

    const slot = productRoot.querySelector("[data-free-gift-media-badge]");
    if (slot) slot.remove();
  }

  _selectionEntriesFromStore(offerId) {
    const store = window.FreeGiftSelection;
    if (!store) return [];
    return store.get(offerId) || [];
  }

  _persistSelection(offerId, entries) {
    this._writeSelection(offerId, entries);
    this._updateFormGuard();
  }

  _syncPickerSelectionsFromDom() {
    const GiftRow = window.FreeGiftGiftRow;

    this.querySelectorAll("[data-free-gift-offer-id]").forEach((block) => {
      const offerId = block.dataset.freeGiftOfferId;
      if (!offerId) return;

      const picker = block.querySelector("[data-free-gift-picker]");
      if (!picker) return;

      const productIdFromRow = (input) => {
        const row = input?.closest(".free-gift-offer__picker-row");
        const fromRow = row?.dataset?.giftProductId;
        return fromRow ? Number(fromRow) : undefined;
      };

      const checkedRadio = picker.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        const variantId = GiftRow?.variantIdFromPickerInput
          ? GiftRow.variantIdFromPickerInput(checkedRadio)
          : Number(checkedRadio.value);
        if (variantId) {
          const productId = productIdFromRow(checkedRadio);
          this._writeSelection(
            offerId,
            [{ variantId, quantity: 1, productId }],
            { skipReconcile: true }
          );
        }
        return;
      }

      const checkboxes = picker.querySelectorAll(
        'input[type="checkbox"].free-gift-offer__picker-input'
      );
      if (checkboxes.length) {
        const entries = [];
        checkboxes.forEach((input) => {
          if (!input.checked) return;
          const variantId = GiftRow?.variantIdFromPickerInput
            ? GiftRow.variantIdFromPickerInput(input)
            : Number(input.value);
          if (!variantId) return;
          let quantity = 1;
          const qtyInput = input
            .closest(".free-gift-offer__picker-row")
            ?.querySelector(".free-gift-offer__picker-qty-input");
          if (qtyInput) quantity = Number(qtyInput.value) || 1;
          const productId = productIdFromRow(input);
          entries.push({ variantId, quantity, productId });
        });
        this._writeSelection(offerId, entries, {
          skipAdditionalItemsSync: true,
        });
        return;
      }

      const actionRows = picker.querySelectorAll(
        ".free-gift-offer__picker-row--actions"
      );
      if (!actionRows.length) return;

      const actionEntries = [];
      actionRows.forEach((row) => {
        const badge = row.querySelector(".free-gift-offer__picker-qty-badge");
        const quantity =
          badge && !badge.hidden
            ? Number(String(badge.textContent || "").replace(/[^\d]/g, "")) || 0
            : 0;
        const variantId = Number(
          row.dataset.selectedVariantId ||
            row.querySelector(".free-gift-offer__picker-input")?.value ||
            0
        );
        if (!variantId || quantity <= 0) return;
        actionEntries.push({
          variantId,
          quantity,
          productId: row.dataset.giftProductId
            ? Number(row.dataset.giftProductId)
            : undefined,
        });
      });
      this._writeSelection(offerId, actionEntries, {
        skipAdditionalItemsSync: true,
      });
    });
    this._syncAdditionalItemsInputs();
  }

  _writeSelection(offerId, entries, options = {}) {
    window.FreeGiftSelection?.set(offerId, entries);
    if (!options.skipAdditionalItemsSync) this._syncAdditionalItemsInputs();
  }

  _createHiddenInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value);
    return input;
  }

  _mandatoryPickerContext(offer) {
    const Shared = window.FreeGiftShared;
    const { items, subtotalCents } = this._pdpProjectedContext(offer);
    const actualItems = this._cartContext?.items || [];
    const actualSubtotalCents = this._cartContext?.subtotalCents || 0;
    if (Shared.getPdpMandatoryPickerContext) {
      return Shared.getPdpMandatoryPickerContext(
        offer,
        items,
        subtotalCents,
        actualItems,
        actualSubtotalCents
      );
    }
    return {
      showPicker: Shared.requiresPdpGiftSelection?.(
        offer,
        items,
        subtotalCents
      ),
      mode: Shared.getPdpPickerMode?.(offer, items, subtotalCents),
      shortfall: Shared.getGiftShortfall?.(offer, items, subtotalCents),
    };
  }

  _offerRequiresPdpSelection(offer) {
    const Shared = window.FreeGiftShared;
    if (!Shared || !offer) return false;

    if (this._mandatoryPickerContext(offer)?.showPicker) return true;

    return !!Shared.requiresPdpOptionalGiftPicker?.(
      offer,
      this._cartContext?.items || [],
      this._cartContext?.subtotalCents || 0,
      this.dataset.productId,
      this._readAddToCartQuantity()
    );
  }

  _computePdpGiftAdditions() {
    const Shared = window.FreeGiftShared;
    if (!Shared || !Array.isArray(this._offers)) return [];

    const additions = [];
    const store = window.FreeGiftSelection;

    this._offers.forEach((resolved) => {
      const offer = resolved.raw;
      const { items, subtotalCents } = this._pdpProjectedContext(offer);
      const selectedEntries = store?.get(offer.id) || [];
      const requiresSelection = this._offerRequiresPdpSelection(offer);

      if (requiresSelection) {
        const hasSelection = (selectedEntries || []).some(
          (entry) => Number(entry?.variantId) > 0 && Number(entry?.quantity) > 0
        );
        if (!hasSelection) return;

        const fromSelection =
          Shared.getMissingAutoAddFromSelection?.(
            offer,
            items,
            subtotalCents,
            selectedEntries
          ) || [];
        fromSelection.forEach((row) => {
          additions.push({
            variantId: Number(row.variantId),
            quantity: Math.max(1, Number(row.quantity) || 1),
            productId: row.productId ? Number(row.productId) : undefined,
            offerId: offer.id,
          });
        });
        return;
      }

      // Deterministic auto-add path: no picker required.
      if (offer?.autoAddEnabled !== true || offer?.promptToAdd === true) return;

      const missing = Shared.getMissingGifts(offer, items, subtotalCents);
      missing.forEach((row) => {
        let variantId = Number(row.variantId);
        let quantity = Math.max(1, Number(row.quantity) || 1);
        const giftMeta =
          Shared.findGiftProductForVariant?.(
            offer,
            row.variantId,
            row.productId
          ) || Shared.findGiftProduct?.(offer, row.variantId);
        const productId =
          Number(row.productId) ||
          Number(Shared.giftProductId?.(giftMeta)) ||
          undefined;

        // Prefer a customer-chosen variant for this gift product (size/colour).
        if (productId && selectedEntries.length) {
          const override = selectedEntries.find(
            (entry) => Number(entry?.productId) === Number(productId)
          );
          if (override?.variantId) {
            variantId = Number(override.variantId);
            quantity = Math.max(1, Number(override.quantity) || quantity);
          }
        }

        additions.push({
          variantId,
          quantity,
          productId,
          offerId: offer.id,
        });
      });
    });

    return additions.filter((row) => row.variantId && row.quantity > 0);
  }

  _syncAdditionalItemsInputs() {
    const forms = this._productForms();
    if (!forms.length) return [];

    const additions = this._computePdpGiftAdditions();
    forms.forEach((form) => {
      let host = form.querySelector("[data-free-gift-additional-items]");
      if (!host) {
        host = document.createElement("div");
        host.hidden = true;
        host.setAttribute("data-free-gift-additional-items", "");
        form.appendChild(host);
      }
      host.replaceChildren();

      additions.forEach((item, index) => {
        const i = index + 1;
        host.appendChild(
          this._createHiddenInput(
            `shopify-additional-items[${i}][id]`,
            item.variantId
          )
        );
        host.appendChild(
          this._createHiddenInput(
            `shopify-additional-items[${i}][quantity]`,
            item.quantity
          )
        );
        // Do not set parent_id: nested additional items make Shopify treat the
        // qualifying product as a bundle, which 422s when Cart Transform also
        // tries to lineExpand it. Sibling items + a preloaded `_free_gift`
        // line keep the parent a normal product.
        host.appendChild(
          this._createHiddenInput(
            `shopify-additional-items[${i}][properties][_free_gift]`,
            "true"
          )
        );
        if (item.offerId) {
          host.appendChild(
            this._createHiddenInput(
              `shopify-additional-items[${i}][properties][_free_gift_offer_id]`,
              item.offerId
            )
          );
        }
        host.appendChild(
          this._createHiddenInput(
            `shopify-additional-items[${i}][properties][_free_gift_variant_id]`,
            item.variantId
          )
        );
      });
    });

    this._pendingGiftAdditions = additions;
    return additions;
  }

  _giftRowImage(gift, existingSlot) {
    const Templates = window.FreeGiftTemplates;
    const Images = window.FreeGiftImages;
    const slot =
      existingSlot ||
      Templates?.element?.("gift-image-slot") ||
      (() => {
        const span = document.createElement("span");
        span.className = "free-gift-offer__gift-image-slot";
        return span;
      })();

    const mount = () => {
      slot.replaceChildren();
      if (Images?.mountGiftImage) {
        slot.appendChild(
          Images.mountGiftImage({
            image: gift.image,
            title: gift.title,
            variantId: gift.variantId,
            productId: gift.productId,
            variant: gift.variant,
          })
        );
      } else if (gift.image) {
        const img =
          Templates?.element?.("gift-image") || document.createElement("img");
        img.className = "free-gift-offer__gift-image";
        img.src = gift.image.includes("?")
          ? `${gift.image}&width=120`
          : `${gift.image}?width=120`;
        img.alt = gift.title || "";
        slot.appendChild(img);
      }
    };
    mount();
    slot._refreshGiftImage = mount;
    return slot;
  }

  _giftRowCallbacks(gift, offer, optional, localSelections) {
    return {
      onVariantChange: (updatedGift, variantId, row) => {
        const input = row.querySelector(".free-gift-offer__picker-input");
        if (input) input.value = String(variantId);

        if (
          !optional &&
          offer?.id &&
          input?.type === "radio" &&
          input.checked
        ) {
          this._persistSelection(offer.id, [
            { variantId, quantity: 1, productId: updatedGift.productId },
          ]);
        }

        if (localSelections && updatedGift.productId) {
          const prevKeys = Object.keys(localSelections).filter((key) => {
            const prevVariant = Number(key);
            return (gift.variants || []).some(
              (v) =>
                v.id === prevVariant && gift.productId === updatedGift.productId
            );
          });
          prevKeys.forEach((key) => {
            if (Number(key) !== variantId) delete localSelections[key];
          });
          if (localSelections[variantId]) {
            localSelections[variantId] = localSelections[variantId];
          }
        }
      },
    };
  }

  _appendGiftRowDetails(row, gift, offer, options = {}) {
    const GiftRow = window.FreeGiftGiftRow;
    if (!GiftRow?.appendGiftRowDetails) {
      let titleEl = row.querySelector(".free-gift-offer__gift-title");
      if (!titleEl) {
        titleEl = document.createElement("span");
        titleEl.className = "free-gift-offer__gift-title";
        row.appendChild(titleEl);
      }
      titleEl.textContent = gift.title || "";
      return;
    }
    const baseCallbacks = this._giftRowCallbacks(
      gift,
      offer,
      options.optional,
      options.localSelections
    );
    GiftRow.appendGiftRowDetails(
      row,
      gift,
      (cents) => this._formatMoney(cents),
      {
        onVariantChange: (updatedGift, variantId, rowEl) => {
          baseCallbacks.onVariantChange(updatedGift, variantId, rowEl);
          if (options.onVariantChange)
            options.onVariantChange(updatedGift, variantId, rowEl);
        },
      }
    );
  }

  _selectionMatchesGift(storedVariantId, gift) {
    if (!storedVariantId) return false;
    if (Number(storedVariantId) === Number(gift.variantId)) return true;
    return (gift.variants || []).some((v) => v.id === Number(storedVariantId));
  }

  _readAddToCartQuantity() {
    const productRoot = this.closest(".product");
    if (!productRoot) return 1;
    const input = productRoot.querySelector(
      'form[action*="/cart/add"] [name="quantity"]'
    );
    const value = input ? Number(input.value) : 1;
    return value > 0 ? value : 1;
  }

  _pdpProjectedContext(offer) {
    const Shared = window.FreeGiftShared;
    const { items, subtotalCents } = this._cartContext || {
      items: [],
      subtotalCents: 0,
    };
    if (!Shared?.withPdpTriggerItems) return { items, subtotalCents };

    const pageProductId = this.dataset.productId;
    const projectedItems = Shared.withPdpTriggerItems(
      offer,
      items,
      pageProductId,
      this._readAddToCartQuantity()
    );
    return { items: projectedItems, subtotalCents };
  }

  _clonePickerFieldset(legendText) {
    const Templates = window.FreeGiftTemplates;
    const fieldset =
      Templates?.element?.("picker-fieldset") ||
      document.createElement("fieldset");
    fieldset.className = "free-gift-offer__picker";
    let legend = fieldset.querySelector(".free-gift-offer__picker-legend");
    if (!legend) {
      legend = document.createElement("legend");
      legend.className = "free-gift-offer__picker-legend";
      fieldset.prepend(legend);
    }
    legend.textContent = legendText || "";
    return fieldset;
  }

  _fillPickerRowImage(row, gift) {
    const slot = row.querySelector(".free-gift-offer__gift-image-slot");
    const result = this._giftRowImage(gift, slot);
    if (result && !result.isConnected) {
      const input = row.querySelector(".free-gift-offer__picker-input");
      if (input) input.after(result);
      else row.prepend(result);
    }
  }

  _renderPickerChooseOne(offer, block, gifts, options = {}) {
    const Templates = window.FreeGiftTemplates;
    // Legend left empty — choose copy lives on `.free-gift-offer__heading`.
    const fieldset = this._clonePickerFieldset("");
    fieldset.setAttribute("data-free-gift-picker", "");
    const stored = this._selectionEntriesFromStore(offer.id);
    const storedId = stored.length === 1 ? Number(stored[0].variantId) : null;
    const groupName = `free-gift-${offer.id}`;
    const availableGifts = gifts.filter((gift) => gift.available);
    const storedGift = availableGifts.find((gift) =>
      this._selectionMatchesGift(storedId, gift)
    );
    const defaultGift =
      storedGift || (options.preselectFirst ? availableGifts[0] : null);

    availableGifts.forEach((gift) => {
      const row =
        Templates?.element?.("picker-row-radio") ||
        document.createElement("label");
      row.className = "free-gift-offer__picker-row";
      if (gift.productId) row.dataset.giftProductId = String(gift.productId);

      let input =
        Templates?.slot?.(row, "input") ||
        row.querySelector(".free-gift-offer__picker-input");
      if (!input) {
        input = document.createElement("input");
        input.type = "radio";
        input.className = "free-gift-offer__picker-input";
        row.prepend(input);
      }
      input.type = "radio";
      input.name = groupName;
      input.className = "free-gift-offer__picker-input";
      input.value = String(gift.variantId);
      input.checked = gift === defaultGift;
      const persistChoice = () => {
        if (!input.checked) return;
        const vid = Number(row.dataset.selectedVariantId || gift.variantId);
        this._persistSelection(offer.id, [
          { variantId: vid, quantity: 1, productId: gift.productId },
        ]);
      };
      input.addEventListener("change", persistChoice);
      input.addEventListener("input", persistChoice);

      this._fillPickerRowImage(row, gift);
      this._appendGiftRowDetails(row, gift, offer);
      fieldset.appendChild(row);
    });

    block.appendChild(fieldset);
  }

  _renderPickerChooseMany(
    offer,
    block,
    gifts,
    shortfall,
    labels,
    options = {}
  ) {
    const Templates = window.FreeGiftTemplates;
    // Legend left empty — choose copy lives on `.free-gift-offer__heading`.
    const fieldset = this._clonePickerFieldset("");
    fieldset.setAttribute("data-free-gift-picker", "");
    const stored = this._selectionEntriesFromStore(offer.id);
    const selections = window.FreeGiftShared.normalizeSelections(stored);
    const remaining = shortfall.remaining;

    const countEl =
      Templates?.element?.("picker-count") || document.createElement("p");
    countEl.className = "free-gift-offer__picker-count";

    const heading = block.querySelector(".free-gift-offer__heading");
    let header = block.querySelector(".free-gift-offer__offer-header");
    if (!header && heading) {
      header = document.createElement("div");
      header.className = "free-gift-offer__offer-header";
      heading.before(header);
      header.appendChild(heading);
    }
    if (header) header.appendChild(countEl);
    else fieldset.prepend(countEl);

    const updateCount = (map) => {
      const total = Object.values(map).reduce((sum, qty) => sum + qty, 0);
      countEl.textContent = labels.selectedCount
        .replace(/\{\{\s*selected\s*\}\}/g, String(total))
        .replace(/\{\{\s*total\s*\}\}/g, String(remaining));
      return total;
    };

    const localSelections = { ...selections };
    updateCount(localSelections);
    const rowControls = [];

    const persist = () => {
      const entries = Object.entries(localSelections)
        .filter(([, qty]) => qty > 0)
        .map(([variantId, quantity]) => {
          const matchGift = gifts.find(
            (g) =>
              Number(g.variantId) === Number(variantId) ||
              (g.variants || []).some((v) => v.id === Number(variantId))
          );
          return {
            variantId: Number(variantId),
            quantity,
            productId: matchGift?.productId,
          };
        });
      this._persistSelection(offer.id, entries);
    };

    const syncRowQtyBadge = (row, qty) => {
      const badge =
        Templates?.slot?.(row, "qty-badge") ||
        row.querySelector(".free-gift-offer__picker-qty-badge");
      if (!badge) return;
      if (qty > 0) {
        badge.hidden = false;
        badge.textContent = `×${qty}`;
      } else {
        badge.hidden = true;
        badge.textContent = "";
      }
    };

    const syncActionButtons = () => {
      const atLimit = updateCount(localSelections) >= remaining;
      rowControls.forEach(({ gift, addBtn, clearBtn, row, currentVid }) => {
        const qty = localSelections[currentVid()] || 0;
        addBtn.disabled = !gift.available || atLimit;
        clearBtn.hidden = qty <= 0;
        syncRowQtyBadge(row, qty);
      });
    };

    gifts.forEach((gift) => {
      if (!gift.available) return;

      const row =
        Templates?.element?.("picker-row-actions") ||
        document.createElement("div");
      row.className =
        "free-gift-offer__picker-row free-gift-offer__picker-row--actions";
      if (gift.productId) row.dataset.giftProductId = String(gift.productId);

      this._fillPickerRowImage(row, gift);
      this._appendGiftRowDetails(row, gift, offer, {
        localSelections,
        onVariantChange: () => {
          const vid = Number(row.dataset.selectedVariantId || gift.variantId);
          if (localSelections[gift.variantId] && vid !== gift.variantId) {
            localSelections[vid] = localSelections[gift.variantId];
            delete localSelections[gift.variantId];
          }
          gift.variantId = vid;
          syncRowQtyBadge(row, localSelections[vid] || 0);
        },
      });

      let actions =
        Templates?.slot?.(row, "actions") ||
        row.querySelector(".free-gift-offer__picker-actions");
      if (!actions) {
        actions = document.createElement("div");
        actions.className = "free-gift-offer__picker-actions";
        row.appendChild(actions);
      }

      let addBtn =
        Templates?.slot?.(row, "add") ||
        actions.querySelector(".free-gift-offer__picker-add");
      if (!addBtn) {
        addBtn = document.createElement("button");
        addBtn.type = "button";
        addBtn.className = "free-gift-offer__picker-add";
        actions.appendChild(addBtn);
      }
      addBtn.type = "button";
      addBtn.className = "free-gift-offer__picker-add";
      addBtn.textContent = labels.addOne;

      let clearBtn =
        Templates?.slot?.(row, "clear") ||
        actions.querySelector(".free-gift-offer__picker-clear");
      if (!clearBtn) {
        clearBtn = document.createElement("button");
        clearBtn.type = "button";
        clearBtn.className = "free-gift-offer__picker-clear";
        actions.prepend(clearBtn);
      }
      clearBtn.type = "button";
      clearBtn.className = "free-gift-offer__picker-clear";
      clearBtn.textContent = labels.clear;

      const currentVid = () =>
        Number(row.dataset.selectedVariantId || gift.variantId);

      rowControls.push({ gift, addBtn, clearBtn, row, currentVid });

      clearBtn.addEventListener("click", () => {
        delete localSelections[currentVid()];
        syncActionButtons();
        persist();
      });

      addBtn.addEventListener("click", () => {
        if (!gift.available) return;
        if (updateCount(localSelections) >= remaining) return;
        const vid = currentVid();
        localSelections[vid] = (localSelections[vid] || 0) + 1;
        gift.variantId = vid;
        syncActionButtons();
        persist();
      });

      fieldset.appendChild(row);
    });

    syncActionButtons();

    block.appendChild(fieldset);
  }

  _renderPickerIndividual(
    offer,
    block,
    gifts,
    shortfall,
    labels,
    options = {}
  ) {
    const Templates = window.FreeGiftTemplates;
    // Legend left empty — choose copy lives on `.free-gift-offer__heading`.
    const fieldset = this._clonePickerFieldset("");
    fieldset.setAttribute("data-free-gift-picker", "");
    const stored = this._selectionEntriesFromStore(offer.id);
    const selections = { ...window.FreeGiftShared.normalizeSelections(stored) };

    const shortfallByVariant = new Map(
      (shortfall.gifts || []).map((g) => [Number(g.variantId), g.missing])
    );

    const persist = () => {
      const entries = Object.entries(selections)
        .filter(([, qty]) => qty > 0)
        .map(([variantId, quantity]) => {
          const matchGift = gifts.find(
            (g) =>
              Number(g.variantId) === Number(variantId) ||
              (g.variants || []).some((v) => v.id === Number(variantId))
          );
          return {
            variantId: Number(variantId),
            quantity,
            productId: matchGift?.productId,
          };
        });
      this._persistSelection(offer.id, entries);
    };

    gifts.forEach((gift) => {
      if (!gift.available) return;

      const shortfallRow = (shortfall.gifts || []).find(
        (g) =>
          Number(g.variantId) === Number(gift.variantId) ||
          Number(g.productId) === Number(gift.productId)
      );
      const maxQty =
        shortfallRow?.missing ||
        shortfallByVariant.get(Number(gift.variantId)) ||
        0;
      if (maxQty <= 0) return;

      const row =
        Templates?.element?.("picker-row-checkbox") ||
        document.createElement("label");
      row.className = "free-gift-offer__picker-row";
      if (gift.productId) row.dataset.giftProductId = String(gift.productId);

      let checkbox =
        Templates?.slot?.(row, "input") ||
        row.querySelector(".free-gift-offer__picker-input");
      if (!checkbox) {
        checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "free-gift-offer__picker-input";
        row.prepend(checkbox);
      }
      checkbox.type = "checkbox";
      checkbox.className = "free-gift-offer__picker-input";
      checkbox.value = String(gift.variantId);
      checkbox.checked = (selections[gift.variantId] || 0) > 0;
      checkbox.disabled = !gift.available;

      this._fillPickerRowImage(row, gift);
      this._appendGiftRowDetails(row, gift, offer);

      const meta = row.querySelector(".free-gift-offer__gift-meta");
      if (meta) {
        let limitEl = meta.querySelector(".free-gift-offer__gift-limit");
        if (maxQty > 1) {
          if (!limitEl) {
            limitEl = document.createElement("span");
            limitEl.className = "free-gift-offer__gift-limit";
            meta.appendChild(limitEl);
          }
          limitEl.textContent = labels.upToFree.replace(
            /\{\{\s*count\s*\}\}/g,
            String(maxQty)
          );
        } else if (limitEl) {
          limitEl.remove();
        }
      }

      let qtyInput = null;
      if (maxQty > 1) {
        qtyInput =
          Templates?.element?.("picker-qty-input") ||
          document.createElement("input");
        qtyInput.type = "number";
        qtyInput.min = "1";
        qtyInput.max = String(maxQty);
        qtyInput.value = String(Math.max(1, selections[gift.variantId] || 1));
        qtyInput.className = "free-gift-offer__picker-qty-input";
        qtyInput.hidden = !checkbox.checked;
        qtyInput.addEventListener("change", () => {
          let val = Number(qtyInput.value) || 1;
          val = Math.max(1, Math.min(maxQty, val));
          qtyInput.value = String(val);
          const vid = Number(row.dataset.selectedVariantId || gift.variantId);
          selections[vid] = val;
          checkbox.checked = true;
          qtyInput.hidden = false;
          persist();
        });
        row.appendChild(qtyInput);
      }

      const persistCheckbox = () => {
        const vid = Number(row.dataset.selectedVariantId || gift.variantId);
        if (checkbox.checked) {
          const qty = qtyInput
            ? Math.max(1, Math.min(maxQty, Number(qtyInput.value) || 1))
            : 1;
          selections[vid] = qty;
          if (gift.variantId !== vid) delete selections[gift.variantId];
          if (qtyInput) {
            qtyInput.value = String(qty);
            qtyInput.hidden = false;
          }
        } else {
          delete selections[vid];
          delete selections[gift.variantId];
          if (qtyInput) {
            qtyInput.value = "1";
            qtyInput.hidden = true;
          }
        }
        persist();
      };

      checkbox.addEventListener("change", persistCheckbox);
      checkbox.addEventListener("input", persistCheckbox);

      fieldset.appendChild(row);
    });

    block.appendChild(fieldset);
  }

  _appendGiftSummaryDetails(row, gift) {
    const GiftRow = window.FreeGiftGiftRow;
    if (!GiftRow?.appendGiftSummaryDetails) return;
    GiftRow.appendGiftSummaryDetails(row, gift, (cents) =>
      this._formatMoney(cents)
    );
  }

  _renderCompactGiftSummary(
    block,
    gifts,
    offer = null,
    missingRows = [],
    options = {}
  ) {
    const Templates = window.FreeGiftTemplates;
    const list =
      Templates?.element?.("summary-list") || document.createElement("ul");
    list.className = "free-gift-offer__summary list-unstyled";

    const qtyByProduct = new Map();
    const qtyByVariant = new Map();
    (missingRows || []).forEach((row) => {
      const qty = Math.max(1, Number(row.quantity) || 1);
      if (row.productId) qtyByProduct.set(Number(row.productId), qty);
      if (row.variantId) qtyByVariant.set(Number(row.variantId), qty);
    });

    const persistSummarySelections = () => {
      if (!offer?.id) return;
      const entries = Array.from(
        list.querySelectorAll(".free-gift-offer__summary-row")
      )
        .map((row) => {
          const variantId = Number(
            row.dataset.selectedVariantId ||
              row.querySelector(".free-gift-offer__picker-input")?.value ||
              0
          );
          const productId = Number(row.dataset.giftProductId || 0);
          const quantity =
            qtyByProduct.get(productId) || qtyByVariant.get(variantId) || 1;
          if (!variantId) return null;
          return {
            variantId,
            quantity,
            productId: productId || undefined,
          };
        })
        .filter(Boolean);
      this._persistSelection(offer.id, entries);
    };

    gifts.forEach((gift) => {
      const item =
        Templates?.element?.("summary-row") || document.createElement("li");
      item.className =
        "free-gift-offer__summary-row free-gift-offer__picker-row";
      if (gift.productId) item.dataset.giftProductId = String(gift.productId);

      this._fillPickerRowImage(item, gift);

      const hasVariantChoice = (gift.variants || []).length > 1;
      if (hasVariantChoice && offer) {
        this._appendGiftRowDetails(item, gift, offer, {
          onVariantChange: () => persistSummarySelections(),
        });
      } else {
        this._appendGiftSummaryDetails(item, gift);
      }

      list.appendChild(item);
    });

    block.appendChild(list);

    if (offer?.id && options.persist !== false) persistSummarySelections();
  }

  _renderGiftList(block, availableGifts) {
    const list = document.createElement("ul");
    list.className = "free-gift-offer__gifts list-unstyled";

    availableGifts.forEach((gift) => {
      const item = document.createElement("li");
      item.className = "free-gift-offer__gift free-gift-offer__picker-row";
      if (gift.productId) item.dataset.giftProductId = String(gift.productId);

      item.appendChild(this._giftRowImage(gift));
      this._appendGiftRowDetails(item, gift, { id: null }, {});
      list.appendChild(item);
    });

    block.appendChild(list);
  }

  _presentGiftsForNotice(resolved, actualItems) {
    const Shared = window.FreeGiftShared;
    const present =
      Shared.getPresentCartGifts?.(resolved?.raw, actualItems) || [];

    return present.map((row) => {
      const match = (resolved?.gifts || []).find(
        (gift) =>
          Number(gift.productId) === Number(row.productId) ||
          Number(gift.variantId) === Number(row.variantId)
      );
      if (match) {
        return { ...match, title: row.title || match.title };
      }
      return {
        productId: row.productId,
        variantId: row.variantId,
        title: row.title,
        available: true,
      };
    });
  }

  _renderAlreadyInCartNotice(block, gifts = []) {
    const Templates = window.FreeGiftTemplates;
    const notice =
      Templates?.element?.("already-in-cart-notice") ||
      document.createElement("p");
    notice.className = "free-gift-offer__already-in-cart";
    notice.textContent = this._labels().alreadyInCart;
    block.appendChild(notice);

    if (!gifts.length) return;
    // Read-only rows: name the gift already in cart; do not persist ATC selections.
    this._renderCompactGiftSummary(block, gifts, null, [], { persist: false });
  }

  _renderOfferBlock(resolved) {
    const Shared = window.FreeGiftShared;
    const Templates = window.FreeGiftTemplates;
    const offer = resolved.raw;
    const { items, subtotalCents } = this._pdpProjectedContext(offer);
    const actualItems = this._cartContext?.items || [];
    const actualSubtotalCents = this._cartContext?.subtotalCents || 0;
    const labels = this._labels();

    const availableGifts = resolved.gifts.filter((gift) => gift.available);
    if (!availableGifts.length) return null;

    const block =
      Templates?.element?.("offer-block") || document.createElement("div");
    block.className = "free-gift-offer__offer";
    block.dataset.freeGiftOfferId = offer.id;

    const mandatoryCtx = this._mandatoryPickerContext(offer);

    const needsOptionalPicker = Shared.requiresPdpOptionalGiftPicker(
      offer,
      this._cartContext?.items || [],
      this._cartContext?.subtotalCents || 0,
      this.dataset.productId,
      this._readAddToCartQuantity()
    );

    const optionalPickerMode = needsOptionalPicker
      ? Shared.getPdpOptionalPickerMode(
          offer,
          this._cartContext?.items || [],
          this._cartContext?.subtotalCents || 0,
          this.dataset.productId,
          this._readAddToCartQuantity()
        )
      : null;

    const showSelectionPicker =
      Boolean(mandatoryCtx.showPicker) ||
      optionalPickerMode === "choose_one" ||
      optionalPickerMode === "choose_many" ||
      optionalPickerMode === "individual";

    const headingText = showSelectionPicker
      ? labels.chooseHeading
      : labels.blockHeading;

    const heading =
      Templates?.slot?.(block, "heading") ||
      block.querySelector(".free-gift-offer__heading");
    if (heading) heading.textContent = headingText;
    else {
      const h = document.createElement("p");
      h.className = "free-gift-offer__heading";
      h.textContent = headingText;
      block.appendChild(h);
    }

    const showAlreadyInCart = Shared.shouldShowPdpAlreadyInCartNotice
      ? Shared.shouldShowPdpAlreadyInCartNotice(
          offer,
          actualItems,
          actualSubtotalCents,
          items,
          subtotalCents
        )
      : Shared.shouldShowPdpCompactSummary?.(
          offer,
          actualItems,
          actualSubtotalCents,
          items,
          subtotalCents
        );

    if (mandatoryCtx.showPicker) {
      const mode = mandatoryCtx.mode;
      const shortfall = mandatoryCtx.shortfall;
      const pickerGifts = availableGifts;

      if (mode === "choose_one") {
        this._renderPickerChooseOne(offer, block, pickerGifts, {
          preselectFirst: true,
        });
      } else if (mode === "choose_many") {
        this._renderPickerChooseMany(
          offer,
          block,
          pickerGifts,
          shortfall,
          labels
        );
      } else if (mode === "individual") {
        this._renderPickerIndividual(
          offer,
          block,
          pickerGifts,
          shortfall,
          labels
        );
      }
    } else if (needsOptionalPicker) {
      const mode = optionalPickerMode;
      const shortfall = Shared.getGiftShortfall(offer, items, subtotalCents);
      const pickerGifts = availableGifts;

      if (mode === "choose_one") {
        this._renderPickerChooseOne(offer, block, pickerGifts);
      } else if (mode === "choose_many") {
        this._renderPickerChooseMany(
          offer,
          block,
          pickerGifts,
          shortfall,
          labels
        );
      } else if (mode === "individual") {
        this._renderPickerIndividual(
          offer,
          block,
          pickerGifts,
          shortfall,
          labels
        );
      } else if (mode === "optional_single") {
        const missing = Shared.getMissingGifts(offer, items, subtotalCents);
        const first = missing[0];
        const gift =
          availableGifts.find(
            (g) =>
              Number(g.variantId) === Number(first?.variantId) ||
              Number(g.productId) === Number(first?.productId)
          ) || pickerGifts[0];
        if (gift?.available) {
          this._renderCompactGiftSummary(block, [gift], offer, missing);
        }
      }
    } else if (showAlreadyInCart) {
      this._renderAlreadyInCartNotice(
        block,
        this._presentGiftsForNotice(resolved, actualItems)
      );
    } else if (!mandatoryCtx.showPicker && !needsOptionalPicker) {
      // Deterministic auto-add (single gift or fixed multi-gift bundle):
      // show read-only rows for gifts that will be attached on ATC.
      const missing =
        Shared.getMissingGifts?.(offer, items, subtotalCents) || [];
      const summaryGifts = missing
        .map((row) =>
          resolved.gifts.find(
            (g) =>
              Number(g.variantId) === Number(row.variantId) ||
              Number(g.productId) === Number(row.productId)
          )
        )
        .filter((gift) => gift && gift.available);

      if (summaryGifts.length) {
        this._renderCompactGiftSummary(block, summaryGifts, offer, missing);
      } else {
        const presentGifts = this._presentGiftsForNotice(resolved, actualItems);
        if (presentGifts.length) {
          this._renderAlreadyInCartNotice(block, presentGifts);
        }
      }
    }

    return block;
  }

  _render(resolvedOffers) {
    const content = this.querySelector("[data-free-gift-content]");
    if (!content) return;

    const stockedOffers = (resolvedOffers || []).filter((offer) =>
      offer?.gifts?.some((gift) => gift.available)
    );

    this._clearSelectionsWhenGiftsAreInCart(stockedOffers);

    if (!stockedOffers.length) {
      this._removeBlock();
      return;
    }

    content.replaceChildren();
    this._hideSkeleton();
    content.hidden = false;
    this.classList.remove("free-gift-offer--unavailable");

    this._syncMediaBadge(true);

    stockedOffers.forEach((offer) => {
      const block = this._renderOfferBlock(offer);
      if (block) content.appendChild(block);
    });

    if (!content.children.length) {
      this._removeBlock();
      return;
    }

    this._updateFormGuard();
  }

  _clearSelectionsWhenGiftsAreInCart(resolvedOffers) {
    const Shared = window.FreeGiftShared;
    const store = window.FreeGiftSelection;
    if (!Shared || !store) return;

    const items = this._cartContext?.items || [];
    const subtotalCents = this._cartContext?.subtotalCents || 0;

    resolvedOffers.forEach((resolved) => {
      const offer = resolved?.raw;
      if (!offer?.id) return;
      const shortfall = Shared.getGiftShortfall?.(offer, items, subtotalCents);
      if (!shortfall || shortfall.remaining > 0) return;
      const present = (shortfall.gifts || []).some(
        (gift) => Number(gift.present) > 0
      );
      if (present) store.clear(offer.id);
    });
  }

  async _refreshFromCart() {
    if (!this._offers?.length) return;

    const cart = await this._fetchCart();
    this._cartContext = this._cartSnapshot(cart);
    this._render(this._offers);
  }

  _bindCartListener() {
    if (
      typeof subscribe !== "function" ||
      typeof PUB_SUB_EVENTS === "undefined"
    )
      return;

    this._cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === window.FreeGiftShared?.SOURCE) return;
      // Product-form ATC often lands the parent line first, then gifts via a
      // follow-up add. Skip the intermediate cart paint — `_bindPdpGiftOnProductAdd`
      // refreshes once after gifts are synced.
      if (event?.source === "product-form") return;
      this._refreshFromCart();
    });
  }

  _offersRequiringValidSelection() {
    const Shared = window.FreeGiftShared;
    if (!Shared || !this._offers?.length) return [];

    return this._offers
      .map((resolved) => resolved.raw)
      .filter((offer) => this._offerRequiresPdpSelection(offer));
  }

  _selectionIsValid() {
    const Shared = window.FreeGiftShared;
    const store = window.FreeGiftSelection;
    if (!Shared || !store) return true;

    const pending = this._offersRequiringValidSelection();
    if (!pending.length) return true;

    return pending.every((offer) => {
      const { items, subtotalCents } = this._pdpProjectedContext(offer);
      return Shared.hasValidPdpSelection(
        offer,
        items,
        subtotalCents,
        store.get(offer.id),
        this._cartContext?.items || [],
        this._cartContext?.subtotalCents || 0
      );
    });
  }

  _updateFormGuard() {
    const productRoot = this.closest(".product");
    if (!productRoot) return;

    this._syncPickerSelectionsFromDom();

    let notice = productRoot.querySelector("[data-free-gift-selection-notice]");
    const needsSelection = this._offersRequiringValidSelection().length > 0;
    const valid = this._selectionIsValid();

    if (!needsSelection || valid) {
      if (notice) notice.hidden = true;
      productRoot.querySelectorAll("[data-free-gift-submit]").forEach((btn) => {
        btn.disabled = false;
      });
      return;
    }

    if (!notice) {
      const Templates = window.FreeGiftTemplates;
      notice =
        Templates?.element?.("selection-notice") || document.createElement("p");
      notice.className = "free-gift-offer__selection-notice";
      notice.setAttribute("data-free-gift-selection-notice", "");
      this.insertAdjacentElement("afterend", notice);
    }

    notice.textContent = this._labels().selectionRequired;
    notice.hidden = false;

    productRoot.querySelectorAll("[data-free-gift-submit]").forEach((btn) => {
      btn.disabled = true;
    });
  }

  _productForms() {
    const productRoot = this.closest(".product");
    if (!productRoot) return [];
    return Array.from(
      productRoot.querySelectorAll('form[action*="/cart/add"]')
    );
  }

  async _addGiftsToCart(entries) {
    if (!entries?.length) return true;
    const cartAddUrl =
      (window.routes && window.routes.cart_add_url) || "/cart/add";
    const payload = {
      items: entries.map((entry) => ({
        id: Number(entry.variantId),
        quantity: Number(entry.quantity) || 1,
        properties: {
          _free_gift: "true",
          _free_gift_offer_id: entry.offerId || "",
          _free_gift_variant_id: String(entry.variantId),
        },
      })),
    };

    const response = await fetch(cartAddUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    return response.ok;
  }

  async _preloadGiftsThenResubmit(form, additions) {
    const ok = await this._addGiftsToCart(additions);
    if (!ok) return;

    this._pendingGiftAdditions = [];
    this._giftsPreloadedForSubmit = true;
    await this._refreshFromCart();

    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }

  _bindPdpGiftOnProductAdd() {
    this._productForms().forEach((form) => {
      if (form.dataset.freeGiftPdpAddBound) return;
      form.dataset.freeGiftPdpAddBound = "true";

      form.addEventListener(
        "submit",
        (event) => {
          this._syncPickerSelectionsFromDom();
          const additions = this._syncAdditionalItemsInputs();

          // Parent add after gifts were attached — skip a second gift POST.
          if (this._giftsPreloadedForSubmit) {
            this._giftsPreloadedForSubmit = false;
            this._pendingGiftAdditions = [];
            return;
          }

          if (!additions.length) return;

          // Cart Transform lineExpands the qualifying product when no gift
          // line is present yet, which Shopify rejects as a bundle add.
          // Attach `_free_gift` lines first so the parent add stays a normal product.
          event.preventDefault();
          event.stopImmediatePropagation();
          this._preloadGiftsThenResubmit(form, additions);
        },
        { capture: true }
      );
    });

    if (
      typeof subscribe !== "function" ||
      typeof PUB_SUB_EVENTS === "undefined"
    )
      return;

    this._giftAddUnsubscribe = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      async (event) => {
        if (event?.source !== "product-form") return;

        const additions = this._pendingGiftAdditions || [];
        if (additions.length) {
          const ok = await this._addGiftsToCart(additions);
          if (ok) {
            this._pendingGiftAdditions = [];
            [
              ...new Set(additions.map((row) => row.offerId).filter(Boolean)),
            ].forEach((offerId) => window.FreeGiftSelection?.clear(offerId));

            // Product-form already painted the drawer from the parent add.
            // Publish after gifts land so cart-drawer-items refetches the section.
            if (typeof publish === "function") {
              const cartUrl =
                (window.routes && window.routes.cart_url) || "/cart";
              const cartData = await fetch(`${cartUrl}.js`, {
                headers: { Accept: "application/json" },
              })
                .then((response) => (response.ok ? response.json() : null))
                .catch(() => null);
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source:
                  window.FreeGiftShared?.SOURCE || "free-gift-controller",
                cartData,
              });
            }
          }
        }

        // Always refresh after the product-form ATC settles so we never paint
        // the parent-only cart that exists before gift lines are present.
        await this._refreshFromCart();
      }
    );
  }

  _bindProductFormGuard() {
    const productRoot = this.closest(".product");
    if (!productRoot) return;

    const forms = productRoot.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach((form) => {
      form
        .querySelectorAll('[type="submit"], button[name="add"]')
        .forEach((btn) => {
          btn.setAttribute("data-free-gift-submit", "");
        });

      if (form.dataset.freeGiftGuardBound) return;
      form.dataset.freeGiftGuardBound = "true";

      form.addEventListener(
        "submit",
        (event) => {
          this._syncPickerSelectionsFromDom();
          this._updateFormGuard();
          if (!this._selectionIsValid()) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    });

    this._updateFormGuard();
  }

  _syncMediaBadge(show) {
    const productRoot = this.closest(".product");
    if (!productRoot) return;

    const slot = productRoot.querySelector("[data-free-gift-media-badge]");
    if (!slot) return;

    if (!show) {
      slot.remove();
      return;
    }

    slot.replaceChildren();
    const Templates = window.FreeGiftTemplates;
    const badge =
      Templates?.element?.("media-badge") || document.createElement("span");
    badge.className = "badge free-gift-offer__badge";
    badge.textContent = this.dataset.badgeLabel || "Free Gift";
    slot.appendChild(badge);
  }
}

if (!customElements.get("free-gift-offer")) {
  customElements.define("free-gift-offer", FreeGiftOffer);
}

/**
 * Standalone media badge when only the free_gift_media_badge block is used.
 */
class FreeGiftMediaBadge extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    if (!window.FreeGiftShared) {
      window.addEventListener(
        "load",
        () => {
          if (this._initialized) return;
          this._initialized = true;
          this._init();
        },
        { once: true }
      );
      return;
    }
    this._initialized = true;
    this._init();
  }

  async _init() {
    if (document.querySelector("free-gift-offer")) {
      this.remove();
      return;
    }

    try {
      const Shared = window.FreeGiftShared;
      if (!Shared) return;

      const offers = this._parseOffers();
      if (!offers.length) {
        this.remove();
        return;
      }

      const resolvedOffers = await Promise.all(
        offers.map((offer) => this._resolveOffer(offer))
      );
      const validOffers = resolvedOffers.filter(Boolean);
      if (!validOffers.length) {
        this.remove();
        return;
      }

      const anyAvailable = validOffers.some((offer) =>
        offer.gifts.some((gift) => gift.available)
      );

      if (!anyAvailable) {
        this.remove();
        return;
      }

      this.replaceChildren();
      const Templates = window.FreeGiftTemplates;
      const badge =
        Templates?.element?.("media-badge") || document.createElement("span");
      badge.className = "badge free-gift-offer__badge";
      badge.textContent = this.dataset.badgeLabel || "Free Gift";
      this.appendChild(badge);
    } catch (error) {
      console.warn("[free-gift-media-badge]", error);
      this.remove();
    }
  }

  _parseOffers() {
    const script = this.querySelector("[data-free-gift-offers]");
    if (!script) return [];
    return window.FreeGiftShared
      ? window.FreeGiftShared.parseOffersJson(script.textContent.trim())
      : [];
  }

  async _resolveOffer(offer) {
    const Shared = window.FreeGiftShared;
    if (!Shared) return null;

    let resolvedGifts = [];
    try {
      resolvedGifts = Shared.resolveOfferGifts
        ? await Shared.resolveOfferGifts(offer)
        : [];
    } catch {
      resolvedGifts = [];
    }

    if (!resolvedGifts.length) return null;

    return { gifts: resolvedGifts };
  }
}

if (!customElements.get("free-gift-media-badge")) {
  customElements.define("free-gift-media-badge", FreeGiftMediaBadge);
}
